import React from 'react';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size' | 'required'> {
  options: SelectOption[];
  placeholder?: string;
  label?: string;
  error?: string;
  hint?: string;
  size?: 'sm' | 'md';
  required?: boolean;
}

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      options,
      placeholder = 'Select...',
      label,
      error,
      hint,
      className = '',
      size = 'md',
      required,
      ...rest
    },
    ref
  ) => {
    const sizeClasses = {
      sm: 'h-8 text-xs',
      md: 'h-9 text-sm',
    };

    return (
      <div className={`relative ${className}`}>
        {label && (
          <label className="block text-xs font-medium text-neutral-500 mb-1.5">
            {label}
            {required && <span className="text-danger-500 ml-0.5">*</span>}
          </label>
        )}
        <select
          ref={ref}
          className={`w-full px-3 bg-surface border rounded-lg focus:outline-none
                     transition-all duration-fast ${sizeClasses[size]}
                     ${error
                       ? 'border-danger-500 focus:border-danger-500'
                       : 'border-neutral-200 focus:border-neutral-400'
                     }`}
          {...rest}
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {error && <p className="absolute left-0 mt-0.5 text-xs text-danger-600">{error}</p>}
        {hint && !error && <p className="absolute left-0 mt-0.5 text-xs text-neutral-500">{hint}</p>}
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Select;
